import React, { useState } from "react";
import { Box, IconButton, Link as MuiLink, Typography, styled } from "@mui/material";
import { Link } from "react-router-dom";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CookiePreferencesDialog from "../CookieConsent/CookiePreferencesDialog";
import { CONSENT_TEXTS } from "../../pages/shared/constants";
const FooterWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.common.white,
  width: "100%",
}));

const FooterGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.spacing(4),
  padding: theme.spacing(5, 3),
  [theme.breakpoints.up("sm")]: {
    gridTemplateColumns: "1fr 1fr",
  },
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  position: "relative",
  paddingBottom: theme.spacing(1),
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: 30,
    height: 2,
    backgroundColor: theme.palette.common.white,
  },
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.grey[300],
  textDecoration: "none",
  fontSize: theme.typography.body2.fontSize,
  display: "block",
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  "&:hover": {
    color: theme.palette.common.white,
  },
}));

/** Styled to match `FooterLink`, because it sits in the same run of links — but it is a `<button>`:
 *  it opens a dialog rather than navigating, and a link that goes nowhere is a lie to a screen
 *  reader. */
const FooterLinkButton = styled("button")(({ theme }) => ({
  color: theme.palette.grey[300],
  background: "none",
  border: "none",
  padding: theme.spacing(0.5, 0),
  font: "inherit",
  fontSize: theme.typography.body2.fontSize,
  textAlign: "left",
  cursor: "pointer",
  display: "block",
  "&:hover": {
    color: theme.palette.common.white,
  },
}));

const BottomBar = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[800],
  textAlign: "center",
  padding: theme.spacing(1.5, 2),
}));

const PRIVACY_LINKS = [
  { label: "Quiénes somos", hash: "#quienes-somos" },
  { label: "Datos que recopilamos", hash: "#datos" },
  { label: "Finalidad del tratamiento", hash: "#finalidad" },
  { label: "Base legal", hash: "#base-legal" },
  { label: "Proveedores y terceros", hash: "#terceros" },
  { label: "Conservación", hash: "#conservacion" },
  { label: "Tus derechos", hash: "#derechos" },
  { label: "Seguridad", hash: "#seguridad" },
  { label: "Transferencias internacionales", hash: "#transferencias" },
  { label: "Cookies", hash: "#cookies" },
  { label: "Cambios a esta política", hash: "#cambios" },
  { label: "Contacto", hash: "#contacto" },
];

const CONDITIONS_LINKS = [
  { label: "Aceptación", hash: "#aceptacion" },
  { label: "Ámbito de servicio", hash: "#ambito" },
  { label: "Cuentas y autenticación", hash: "#cuentas" },
  { label: "Compras, pagos y notificaciones", hash: "#compras" },
  { label: "Agendamiento y cancelaciones", hash: "#agendamiento" },
  { label: "Servicios educativos", hash: "#educativo" },
  { label: "Conducta prohibida", hash: "#conducta" },
  { label: "Propiedad intelectual", hash: "#propiedad" },
  { label: "Limitación de responsabilidad", hash: "#responsabilidad" },
  { label: "Garantías y disponibilidad", hash: "#garantias" },
  { label: "Modificaciones", hash: "#modificaciones" },
  { label: "Terminación", hash: "#terminacion" },
  { label: "Jurisdicción", hash: "#jurisdiccion" },
  { label: "Contacto", hash: "#contacto" },
];

const PageFooter: React.FC = () => {
  const [prefsOpen, setPrefsOpen] = useState(false);

  return (
    <FooterWrapper>
      <FooterGrid>
        <Box>
          <SectionTitle variant="h6">Política de Privacidad</SectionTitle>
          {PRIVACY_LINKS.map((link) => (
            <FooterLink key={link.hash} to={`/politica-de-privacidad${link.hash}`}>
              {link.label}
            </FooterLink>
          ))}
          {/* Withdrawing consent has to be as reachable as giving it was (EB-559), so this sits on
              every page, right under the cookies section of the policy it belongs to. */}
          <FooterLinkButton type="button" onClick={() => setPrefsOpen(true)}>
            {CONSENT_TEXTS.footerLink}
          </FooterLinkButton>
        </Box>

        <Box>
          <SectionTitle variant="h6">Condiciones del servicio</SectionTitle>
          {CONDITIONS_LINKS.map((link) => (
            <FooterLink key={link.hash} to={`/condiciones-del-servicio${link.hash}`}>
              {link.label}
            </FooterLink>
          ))}
        </Box>

        <Box>
          <SectionTitle variant="h6">Síguenos</SectionTitle>
          <Box display="flex" gap={1} mt={1}>
            <IconButton
              component={MuiLink}
              href="https://www.instagram.com/entornobiai"
              target="_blank"
              rel="noopener"
              sx={{ color: "brand.instagram" }}
            >
              <InstagramIcon />
            </IconButton>
            <IconButton
              component={MuiLink}
              href="https://www.facebook.com/entornobiai"
              target="_blank"
              rel="noopener"
              sx={{ color: "brand.facebook" }}
            >
              <FacebookIcon />
            </IconButton>
            <IconButton
              component={MuiLink}
              href="https://wa.me/573128014133"
              target="_blank"
              rel="noopener"
              sx={{ color: "brand.whatsapp" }}
            >
              <WhatsAppIcon />
            </IconButton>
          </Box>
          <Box display="flex" alignItems="center" gap={1} mt={2}>
            <LocationOnIcon sx={{ color: "brand.location" }} />
            <Typography variant="body2" color="grey.300">
              Cl. 18 #20 - 71, Yarumal, Antioquia
            </Typography>
          </Box>
        </Box>
      </FooterGrid>

      <BottomBar>
        <Typography variant="caption" color="grey.400">
          {new Date().getFullYear()} Todos los derechos reservados
        </Typography>
      </BottomBar>

      <CookiePreferencesDialog open={prefsOpen} onClose={() => setPrefsOpen(false)} />
    </FooterWrapper>
  );
};

export default PageFooter;
